import { useEffect, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Flex } from '@radix-ui/themes';

import { Table } from './CampaignsTable/Table.jsx';
import { CampaignsHeader } from './CampaignsTable/CampaignsHeader.jsx';
import { useNotification } from '../../../shared/Notification/useNotification';
import { ActionCableContext } from '../../../lib/actionCableContext';
import { usePlans } from '../../../lib/hooks/usePlans';
import useLocalStorage from '../../../lib/hooks/useLocalStorage';
import { useURLParam } from '../../../lib/hooks/useURLParam';
import LoadingComponent from '../../../shared/LoadingComponent/LoadingComponent.jsx';
import CalendarView from './CampaignsTable/CalendarView';
import CreateHighlightModal from './CampaignsTable/CreateHighlightModal.jsx';
import { UpgradeBanner } from '../../../shared/UpgradeBanner/index.jsx';

const VIEW_TYPES = {
  CALENDAR: 'calendar',
  LIST: 'list',
};

export const CampaignsTable = () => {
  const { t } = useTranslation('campaigns');
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();
  const cable = useContext(ActionCableContext);
  const { plans, isFetching } = usePlans();
  const location = useLocation();

  // Get view parameter from URL with validation
  const [getViewFromURL, setViewInURL] = useURLParam(
    'view',
    Object.values(VIEW_TYPES),
  );

  const [initialState, setInitialState] = useLocalStorage(
    'campaignsActiveView',
    VIEW_TYPES.LIST,
  );

  // Initialize state from URL first, then localStorage
  const [activeView, setActiveView] = useState(() => {
    const viewFromURL = getViewFromURL();
    if (viewFromURL) {
      setInitialState(viewFromURL);
      return viewFromURL;
    }
    return initialState;
  });

  const [isHighlightModalOpen, setIsHighlightModalOpen] = useState(false);

  // Update view when URL changes
  useEffect(() => {
    const viewFromURL = getViewFromURL();
    if (viewFromURL && viewFromURL !== activeView) {
      setActiveView(viewFromURL);
      setInitialState(viewFromURL);
    }
  }, [location.search, activeView, setInitialState, getViewFromURL]);

  useEffect(() => {
    const channel = cable.subscriptions.create(
      {
        channel: 'CampaignChannel',
      },
      {
        received: data => {
          const { template, status, errors } = data;

          if (status === 'error') {
            showError({
              content: errors?.length
                ? errors.join(' ')
                : t('campaignNotifications.error'),
            });
            return;
          }

          const msg = template
            ? t('campaignNotifications.templateCreated')
            : t('campaignNotifications.copied');

          showSuccess({ content: msg });
          queryClient.invalidateQueries(['campaigns']);
          queryClient.invalidateQueries(['campaigns-by-year']);
        },
      },
    );
    return () => {
      channel.unsubscribe();
    };
  }, [cable, queryClient, showError, showSuccess, t]);

  const handleOpenHighlightModal = () => {
    setIsHighlightModalOpen(true);
  };

  const handleCloseHighlightModal = () => {
    setIsHighlightModalOpen(false);
  };

  const handleViewToggle = viewType => {
    setActiveView(viewType);
    setInitialState(viewType);
    setViewInURL(viewType);

    const mixpanelData = {
      userName: window.Airbo.Utils.currentUser().user_name,
      boardName: window.Airbo.Utils.currentBoard().name,
      viewType,
    };

    window.Airbo.Utils.ping('User toggled Highlights view', mixpanelData);
  };

  const shouldShowUpgradeBanner = campaignInfo => {
    if (!campaignInfo) return false;

    const { campaignLimit, unlimitedCampaigns } = campaignInfo;

    if (unlimitedCampaigns) return false;

    return campaignLimit;
  };

  // Paid orgs can keep launching after their credits run out; the banner
  // should read as informational in that state rather than as a block.
  const isOverLimitButAllowed = campaignInfo =>
    Boolean(campaignInfo) &&
    campaignInfo.remainingCampaignCount === 0 &&
    campaignInfo.canMakeCampaignLive;

  if (isFetching) return <LoadingComponent />;

  return (
    <Flex direction="column" gap="3" p="5" mx="auto">
      {shouldShowUpgradeBanner(plans?.campaignInfo) && (
        <UpgradeBanner
          icon="fa-regular fa-star"
          titleKey="campaign.title"
          countValue={plans?.campaignInfo.remainingCampaignCount}
          countLimit={plans?.campaignInfo.campaignLimit}
          textContent={(t, Trans) => (
            <>
              <Trans
                i18nKey={
                  isOverLimitButAllowed(plans?.campaignInfo)
                    ? 'campaign.textOverLimit'
                    : 'campaign.text1'
                }
                count={plans?.campaignInfo.campaignLimit}
                components={{ strong: <strong /> }}
                values={{
                  limit: plans?.campaignInfo.campaignLimit,
                  pluralS: plans?.campaignInfo.campaignLimit === 1 ? '' : 's',
                }}
                ns="upgradeBanners"
              />
            </>
          )}
        />
      )}
      <CampaignsHeader
        activeView={activeView}
        onViewToggle={handleViewToggle}
        onOpenHighlightModal={handleOpenHighlightModal}
      />

      {activeView === VIEW_TYPES.CALENDAR ? (
        <CalendarView hideBroadcasts />
      ) : (
        <Table />
      )}
      <CreateHighlightModal
        isOpen={isHighlightModalOpen}
        onClose={handleCloseHighlightModal}
        onCreateHighlight={handleCloseHighlightModal}
      />
    </Flex>
  );
};
